import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControlLabel,
  Switch,
  Typography,
  Box,
  Stack,
  Divider,
  TextField,
  Alert
} from '@mui/material';
import Iconify from '../iconify';
import { 
  getNotificationPreferences, 
  saveNotificationPreferences,
  requestNotificationPermission 
} from '../../utils/notificationUtils';

const NotificationPreferences = ({ open, onClose }) => {
  const [preferences, setPreferences] = useState({
    soundEnabled: true,
    browserNotificationsEnabled: true,
    dndEnabled: false,
    dndStart: '22:00',
    dndEnd: '08:00'
  });

  const [notificationPermission, setNotificationPermission] = useState('default');

  useEffect(() => {
    if (open) {
      // Load preferences from localStorage
      const savedPrefs = getNotificationPreferences();
      setPreferences({
        soundEnabled: savedPrefs.soundEnabled !== false,
        browserNotificationsEnabled: savedPrefs.browserNotificationsEnabled !== false,
        dndEnabled: savedPrefs.dndEnabled === true,
        dndStart: savedPrefs.dndStart || '22:00',
        dndEnd: savedPrefs.dndEnd || '08:00'
      });

      // Check notification permission
      if ('Notification' in window) {
        setNotificationPermission(Notification.permission);
      }
    }
  }, [open]);

  const handleToggle = (key) => {
    setPreferences(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleTimeChange = (key, value) => {
    setPreferences(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleRequestPermission = async () => {
    const granted = await requestNotificationPermission();
    if (granted) {
      setNotificationPermission('granted');
      setPreferences(prev => ({ ...prev, browserNotificationsEnabled: true }));
    } else {
      setNotificationPermission('denied');
    }
  };

  const handleSave = () => {
    // Convert time strings to Date objects for storage
    const now = new Date();
    const [startHour, startMin] = preferences.dndStart.split(':');
    const [endHour, endMin] = preferences.dndEnd.split(':');
    
    const dndStartDate = new Date(now);
    dndStartDate.setHours(parseInt(startHour), parseInt(startMin), 0, 0);
    
    const dndEndDate = new Date(now);
    dndEndDate.setHours(parseInt(endHour), parseInt(endMin), 0, 0);

    saveNotificationPreferences({
      soundEnabled: preferences.soundEnabled,
      browserNotificationsEnabled: preferences.browserNotificationsEnabled,
      dndEnabled: preferences.dndEnabled,
      dndStart: dndStartDate.toISOString(),
      dndEnd: dndEndDate.toISOString()
    });

    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Iconify icon="eva:settings-2-fill" />
          <Typography variant="h6">Notification Preferences</Typography>
        </Stack>
      </DialogTitle>
      
      <DialogContent>
        <Stack spacing={3}>
          {/* Sound Notifications */}
          <Box>
            <FormControlLabel
              control={
                <Switch
                  checked={preferences.soundEnabled}
                  onChange={() => handleToggle('soundEnabled')}
                  color="primary"
                />
              }
              label={
                <Stack>
                  <Typography variant="body1" fontWeight={600}>
                    Sound Notifications
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Play a sound when new notifications arrive
                  </Typography>
                </Stack>
              }
            />
          </Box>

          <Divider />

          {/* Browser Notifications */}
          <Box>
            <Stack spacing={1}>
              <FormControlLabel
                control={
                  <Switch
                    checked={preferences.browserNotificationsEnabled && notificationPermission === 'granted'}
                    onChange={() => {
                      if (notificationPermission !== 'granted') {
                        handleRequestPermission();
                      } else {
                        handleToggle('browserNotificationsEnabled');
                      }
                    }}
                    color="primary"
                    disabled={notificationPermission === 'denied'}
                  />
                }
                label={
                  <Stack>
                    <Typography variant="body1" fontWeight={600}>
                      Browser Notifications
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Show browser notifications even when the app is in the background
                    </Typography>
                  </Stack>
                }
              />
              
              {notificationPermission === 'default' && (
                <Alert severity="info" sx={{ mt: 1 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={handleRequestPermission}
                    startIcon={<Iconify icon="eva:bell-fill" />}
                  >
                    Request Permission
                  </Button>
                </Alert>
              )}
              
              {notificationPermission === 'denied' && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  Browser notification permission is denied. Please enable it in your browser settings.
                </Alert>
              )}
              
              {notificationPermission === 'granted' && !preferences.browserNotificationsEnabled && (
                <Alert severity="info" sx={{ mt: 1 }}>
                  Browser notifications are enabled but currently turned off. Toggle the switch to enable them.
                </Alert>
              )}
            </Stack>
          </Box>

          <Divider />

          {/* Do Not Disturb */}
          <Box>
            <FormControlLabel
              control={
                <Switch
                  checked={preferences.dndEnabled}
                  onChange={() => handleToggle('dndEnabled')}
                  color="primary"
                />
              }
              label={
                <Stack>
                  <Typography variant="body1" fontWeight={600}>
                    Do Not Disturb
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Temporarily silence notifications during specified hours
                  </Typography>
                </Stack>
              }
            />
            
            {preferences.dndEnabled && (
              <Stack direction="row" spacing={2} sx={{ mt: 2, ml: 4 }}>
                <TextField
                  label="Start Time"
                  type="time"
                  value={preferences.dndStart}
                  onChange={(e) => handleTimeChange('dndStart', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                />
                <TextField
                  label="End Time"
                  type="time"
                  value={preferences.dndEnd}
                  onChange={(e) => handleTimeChange('dndEnd', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                />
              </Stack>
            )}
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained" color="primary">
          Save Preferences
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NotificationPreferences;

